import { isManager } from "@/lib/access";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ClipboardList, Clock, PauseCircle, HelpCircle, CheckCircle2, AlertTriangle, CalendarClock, ListChecks, Eye, EyeOff, Calendar } from "lucide-react";

// Two dimensions kept visually separate (spec §5)
const STATUS_TILES = [
  { key: "total", label: "Total tasks", icon: ClipboardList, color: "slate", link: "?flat=1" },
  { key: "pending", label: "Pending", icon: HelpCircle, color: "slate", link: "?status=pending" },
  { key: "not_attended", label: "Not attended", icon: EyeOff, color: "rose", link: "?attention=not_attended" },
  { key: "seen_but_idle", label: "Seen but idle", icon: Eye, color: "amber", link: "?attention=seen_idle" },
  { key: "in_progress", label: "In Progress", icon: Clock, color: "amber", link: "?status=in-progress" },
  { key: "awaiting", label: "Awaiting", icon: HelpCircle, color: "violet", link: "?status=awaiting" },
  { key: "on_hold", label: "On Hold", icon: PauseCircle, color: "orange", link: "?status=on-hold" },
  { key: "completed", label: "Completed", icon: CheckCircle2, color: "emerald", link: "?view=completed" },
];
const DEADLINE_TILES = [
  { key: "overdue", label: "Overdue", icon: AlertTriangle, color: "rose", link: "?due=overdue" },
  { key: "due_today", label: "Due today", icon: CalendarClock, color: "amber", link: "?due=today" },
  { key: "due_week", label: "Due this week", icon: Calendar, color: "sky", link: "?due=week" },
];

const COLOR_STYLES = {
  slate: "bg-slate-50 border-slate-200 text-slate-700",
  sky: "bg-sky-50 border-sky-200 text-sky-700",
  amber: "bg-amber-50 border-amber-200 text-amber-800",
  violet: "bg-violet-50 border-violet-200 text-violet-700",
  orange: "bg-orange-50 border-orange-200 text-orange-700",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  rose: "bg-rose-50 border-rose-200 text-rose-700",
};

export default function TaskDashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const promises = [api.get("/tasks/dashboard/stats")];
        if (isManager(user)) promises.push(api.get("/tasks/dashboard/by-user"));
        const results = await Promise.all(promises);
        setStats(results[0].data);
        if (results[1]) setMatrix(results[1].data);
      } catch (e) { toast.error(formatErr(e)); }
      finally { setLoading(false); }
    })();
  }, [user?.role]);

  if (loading || !stats) return <div className="text-sm text-slate-500">Loading dashboard…</div>;

  return (
    <div>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">Workspace · overview</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Task dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Tap any tile to drill into the individual tasks it counts.</p>
      </div>

      {/* Status dimension */}
      <div className="mb-3 flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500">
        <ListChecks className="w-3.5 h-3.5" /> By status
      </div>
      <div data-testid="status-tiles" className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
        {STATUS_TILES.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`tile-${t.key}`}
              onClick={() => nav(`/tasks${t.link}`)}
              className={`text-left p-4 border rounded-md transition hover:shadow-sm ${COLOR_STYLES[t.color]}`}
            >
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <div className="text-[11px] uppercase tracking-wider">{t.label}</div>
              </div>
              <div className="text-3xl font-semibold mono mt-2">{stats[t.key] ?? 0}</div>
            </button>
          );
        })}
      </div>

      {/* Deadline dimension — kept separate per spec §5 */}
      <div className="mb-3 flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500">
        <CalendarClock className="w-3.5 h-3.5" /> By deadline
      </div>
      <div data-testid="deadline-tiles" className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
        {DEADLINE_TILES.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`tile-${t.key}`}
              onClick={() => nav(`/tasks${t.link}`)}
              className={`text-left p-4 border rounded-md transition hover:shadow-sm ${COLOR_STYLES[t.color]}`}
            >
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <div className="text-[11px] uppercase tracking-wider">{t.label}</div>
              </div>
              <div className="text-3xl font-semibold mono mt-2">{stats[t.key] ?? 0}</div>
            </button>
          );
        })}
      </div>

      {isManager(user) && matrix && (
        <div className="bg-white border border-slate-200 rounded-md">
          <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold flex items-center gap-2"><ListChecks className="w-4 h-4" /> Per-user task matrix</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2">User</th>
                  <th className="text-left px-4 py-2">Role</th>
                  <th className="text-right px-3 py-2">Total</th>
                  <th className="text-right px-3 py-2">Pending</th>
                  <th className="text-right px-3 py-2">Attended</th>
                  <th className="text-right px-3 py-2">In Progress</th>
                  <th className="text-right px-3 py-2">Awaiting</th>
                  <th className="text-right px-3 py-2">On Hold</th>
                  <th className="text-right px-3 py-2">Done</th>
                  <th className="text-right px-3 py-2">Overdue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {matrix.map((row) => (
                  <tr key={row.user_id} data-testid={`matrix-row-${row.user_id}`} className="hover:bg-slate-50">
                    <td className="px-4 py-2 font-medium">{row.user_name}</td>
                    <td className="px-4 py-2 text-xs text-slate-500 capitalize">{row.role.replace("_", " ")}</td>
                    {["total", "pending", "attended", "in_progress", "awaiting", "on_hold", "completed", "overdue"].map((k) => (
                      <td key={k} className="text-right px-3 py-2 mono">
                        <button data-testid={`matrix-${row.user_id}-${k}`} onClick={() => nav(`/tasks?assignee=${row.user_id}${k === "completed" ? "&view=completed" : k !== "total" && k !== "overdue" ? `&status=${k.replace("_", "-")}` : ""}`)}
                                className={`text-inherit ${row[k] > 0 ? "hover:underline" : "text-slate-300"}`}>{row[k]}</button>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
